import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { DarkTheme, LightTheme } from '../themes';

type ThemeName = 'light' | 'dark';

interface IThemeContextData {
  themeName: ThemeName;
  toggleTheme: () => void;
}

const ThemeContext = createContext({} as IThemeContextData);
export const useAppThemeContext = () => useContext(ThemeContext);

const LOCAL_STORAGE_KEY__THEME = 'APP_THEME';

interface IAppThemeProviderProps {
  children: React.ReactNode;
}

export const AppThemeProvider: React.FC<IAppThemeProviderProps> = ({ children }) => {
  const [themeName, setThemeName] = useState<ThemeName>(() => {
    const storedTheme = localStorage.getItem(LOCAL_STORAGE_KEY__THEME);
    return storedTheme === 'light' ? 'light' : 'dark';
  });

  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY__THEME, themeName);
  }, [themeName]);

  const toggleTheme = useCallback(() => {
    setThemeName(oldThemeName => (oldThemeName === 'light' ? 'dark' : 'light'));
  }, []);

  const theme = useMemo(() => (themeName === 'light' ? LightTheme : DarkTheme), [themeName]);

  return (
    <ThemeContext.Provider value={{ themeName, toggleTheme }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};
